import React, { useState, useMemo } from "react";
import {
  X,
  MoreVertical,
  Edit2,
  Pause,
  Copy,
  Ban,
  Trash2,
  Gift,
  Tag,
  CalendarCheck,
  Sparkles,
  Truck,
  Headphones,
  Search,
  Filter,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  TrendingUp,
  ChevronDown,
  Calendar,
  UserPlus,
  Play,
} from "lucide-react";
import AhmedAvatar from "../../../assets/images/alert2 1 (1).png"; 
import "./TierDetailsModal.css";

const BENEFITS_POOL = [
  {
    icon: Gift,
    label: "Double Points",
    desc: "Earn 2 points for every 1 EGP spent",
  },
  {
    icon: Tag,
    label: "10% Discount",
    desc: "Get 10% discount on all services",
  },
  {
    icon: CalendarCheck,
    label: "Priority Booking",
    desc: "Get priority access to booking",
  },
  {
    icon: Sparkles,
    label: "Free Car Wash",
    desc: "1 free car wash every month",
  },
  {
    icon: Truck,
    label: "Free Delivery",
    desc: "Free delivery on all parts orders",
  },
  {
    icon: Headphones,
    label: "Dedicated Support",
    desc: "Priority support from our team",
  },
  { icon: Gift, label: "Birthday Gift", desc: "Special gift on your birthday" },
  {
    icon: Tag,
    label: "Extended Warranty",
    desc: "Extra 6 months on all repairs",
  },
  {
    icon: CalendarCheck,
    label: "VIP Lounge Access",
    desc: "Access to our VIP waiting lounge",
  },
  {
    icon: Sparkles,
    label: "Free Annual Checkup",
    desc: "One free full checkup every year",
  },
  {
    icon: Truck,
    label: "Concierge Service",
    desc: "Personal concierge for bookings",
  },
  {
    icon: Headphones,
    label: "Exclusive Events",
    desc: "Invites to exclusive brand events",
  },
];

const ACTIVITY_LOG = [
  {
    icon: Edit2,
    color: "#A855F7",
    bg: "#F3E8FF",
    title: "Benefits Updated",
    desc: "8 benefits were updated",
    by: "Sara Ahmed",
    date: "Jul 15, 2026 - 02:35pm",
  },
  {
    icon: UserPlus,
    color: "#16A34A",
    bg: "#E6F7F0",
    title: "New Members Added",
    desc: "12 new members joined",
    by: "Ahmed Mohamed",
    date: "Jul 15, 2026 - 02:35pm",
  },
  {
    icon: Edit2,
    color: "#A855F7",
    bg: "#F3E8FF",
    title: "Tier Rules Updated",
    desc: "Upgrade rule was modified",
    by: "Ahmed Mohamed",
    date: "Jul 15, 2026 - 02:35pm",
  },
  {
    icon: Play,
    color: "#14B8A6",
    bg: "#E6F7F4",
    title: "Tier Activated",
    desc: "Tier was activated",
    by: "Ahmed Mohamed",
    date: "Jul 15, 2026 - 02:35pm",
  },
  {
    icon: UserPlus,
    color: "#16A34A",
    bg: "#E6F7F0",
    title: "New Members Added",
    desc: "12 new members joined",
    by: "Ahmed Mohamed",
    date: "Jul 15, 2026 - 02:35pm",
  },
  {
    icon: Edit2,
    color: "#A855F7",
    bg: "#F3E8FF",
    title: "Tier Rules Updated",
    desc: "Upgrade rule was modified",
    by: "Ahmed Mohamed",
    date: "Jul 15, 2026 - 02:35pm",
  },
];

const TIER_DESCRIPTIONS = {
  Bronze: "For new customers just starting their journey with us.",
  Silver: "For loyal customers who engage regularly and earn steady rewards.",
  Gold: "For loyal customers who engage more and earn greater rewards.",
  Platinum: "For our most valued customers who deserve premium treatment.",
};

function extractNumber(str = "") {
  const match = str.match(/[\d,]+/);
  return match ? parseInt(match[0].replace(/,/g, ""), 10) : 0;
}

function buildTierDetails(tier) {
  if (!tier) return null;
  const upgradeNum = extractNumber(tier.upgradeRule);
  const downgradeNum = Math.round(upgradeNum / 2);
  const benefitsList = BENEFITS_POOL.slice(0, tier.benefits || 6);
  const upgrades = Math.round(tier.members * 0.013);
  const downgrades = Math.round(tier.members * 0.0025);
  const netChange = upgrades - downgrades;

  return {
    description: TIER_DESCRIPTIONS[tier.name] || "Tier description goes here.",
    downgradeRule: `Spend less than ${downgradeNum.toLocaleString()} EGP during 90 days`,
    reEvalFrequency: "Every 30 days",
    performance: {
      upgrades,
      upgradesTrend: "+15%",
      downgrades,
      downgradesTrend: "-10%",
      netChange,
      netChangeTrend: "+12%",
    },
    benefits: benefitsList,
    recentMembers: Array.from({ length: 3 }).map(() => ({
      name: "Ahmed Mohamed",
      avatar: AhmedAvatar,
      joined: "May 16, 2025",
    })),
    allMembers: Array.from({ length: 8 }).map(() => ({
      name: "Ahmed Mohamed",
      avatar: AhmedAvatar,
      joined: "May 16, 2025",
    })),
    activityLog: ACTIVITY_LOG,
  };
}

const TABS = ["Overview", "Benefits", "Members", "Activity Log"];

export default function TierDetailsModal({ open, onClose, tier, onAction }) {
  const [activeTab, setActiveTab] = useState("Overview");
  const [menuOpen, setMenuOpen] = useState(false);

  const details = useMemo(() => buildTierDetails(tier), [tier]);

  if (!open || !tier || !details) return null;

  const handleAction = (action) => {
    setMenuOpen(false);
    onAction && onAction(action, tier);
  };

  return (
    <div className="tdm-overlay" onClick={onClose}>
      <div className="tdm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tdm-header">
          <h3 className="tdm-title">Tier Details</h3>
          <button className="tdm-close-btn" onClick={onClose}>
            <X size={16} color="#fff" />
          </button>
        </div>

        <div className="tdm-identity-row">
          <div className="tdm-identity-left">
            <div
              className="tdm-tier-icon"
              style={{ backgroundColor: `${tier.medalColor}22` }}
            >
              <span
                className="tdm-tier-icon-dot"
                style={{ backgroundColor: tier.medalColor }}
              />
            </div>
            <div>
              <div className="tdm-tier-name-row">
                <span className="tdm-tier-name">{tier.name}</span>
                <span className="tdm-active-badge">
                  <span className="tdm-active-dot" /> {tier.status}
                </span>
              </div>
              <span className="tdm-tier-id">
                Tier ID: TIER-{tier.name.toUpperCase()}
              </span>
            </div>
          </div>

          <div className="tdm-menu-wrapper">
            <button
              className="tdm-more-btn"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <MoreVertical size={18} color="#6B7280" />
            </button>
            {menuOpen && (
              <div className="tdm-action-menu">
                <button
                  className="tdm-action-item"
                  onClick={() => handleAction("edit")}
                >
                  <Edit2 size={14} /> Edit
                </button>
                <button
                  className="tdm-action-item tdm-action-warning"
                  onClick={() => handleAction("pause")}
                >
                  <Pause size={14} /> Pause
                </button>
                <button
                  className="tdm-action-item"
                  onClick={() => handleAction("duplicate")}
                >
                  <Copy size={14} /> Duplicate
                </button>
                <button
                  className="tdm-action-item tdm-action-danger"
                  onClick={() => handleAction("disable")}
                >
                  <Ban size={14} /> Disable
                </button>
                <button
                  className="tdm-action-item tdm-action-danger"
                  onClick={() => handleAction("delete")}
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="tdm-stats-row">
          <div className="tdm-stat">
            <span className="tdm-stat-label">Members</span>
            <span className="tdm-stat-value">
              {tier.members.toLocaleString()}
            </span>
            <span className="tdm-stat-sub">{tier.pctOfTotal}% of total</span>
          </div>
          <div className="tdm-stat-divider" />
          <div className="tdm-stat">
            <span className="tdm-stat-label">Avg Stay</span>
            <span className="tdm-stat-value">{tier.avgStay}</span>
          </div>
          <div className="tdm-stat-divider" />
          <div className="tdm-stat">
            <span className="tdm-stat-label">Retention Rate</span>
            <span className="tdm-stat-value">{tier.retention}%</span>
          </div>
        </div>

        <div className="tdm-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              className={`tdm-tab ${activeTab === t ? "active" : ""}`}
              onClick={() => setActiveTab(t)}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="tdm-tab-content">
          {activeTab === "Overview" && (
            <>
              <h4 className="tdm-section-title">Tier Information</h4>

              <div className="tdm-info-list">
                <div className="tdm-info-row">
                  <span className="tdm-info-label">Description</span>
                  <span className="tdm-info-value">{details.description}</span>
                </div>
                <div className="tdm-info-row">
                  <span className="tdm-info-label">Entry Rule</span>
                  <span className="tdm-info-value">
                    {tier.entryRule === "Default"
                      ? "Default"
                      : tier.upgradeRule}
                  </span>
                </div>
                <div className="tdm-info-row">
                  <span className="tdm-info-label">Upgrade Rule</span>
                  <span className="tdm-info-value">{tier.upgradeRule}</span>
                </div>
                <div className="tdm-info-row">
                  <span className="tdm-info-label">Downgrade Rule</span>
                  <span className="tdm-info-value">
                    {details.downgradeRule}
                  </span>
                </div>
                <div className="tdm-info-row">
                  <span className="tdm-info-label">
                    Re-evaluation Frequency
                  </span>
                  <span className="tdm-info-value">
                    {details.reEvalFrequency}
                  </span>
                </div>
              </div>

              <div className="tdm-divider" />

              <h4 className="tdm-section-title">Performance</h4>
              <div className="tdm-performance-row">
                <div className="tdm-perf-cell">
                  <span className="tdm-info-label">Upgrades</span>
                  <span className="tdm-perf-value">
                    {details.performance.upgrades}
                  </span>
                  <span className="tdm-perf-trend up">
                    <ArrowUpRight size={12} />{" "}
                    {details.performance.upgradesTrend}
                  </span>
                </div>
                <div className="tdm-perf-divider" />
                <div className="tdm-perf-cell">
                  <span className="tdm-info-label">Downgrades</span>
                  <span className="tdm-perf-value">
                    {details.performance.downgrades}
                  </span>
                  <span className="tdm-perf-trend down">
                    <ArrowDownRight size={12} />{" "}
                    {details.performance.downgradesTrend}
                  </span>
                </div>
                <div className="tdm-perf-divider" />
                <div className="tdm-perf-cell">
                  <span className="tdm-info-label">Net Change</span>
                  <span className="tdm-perf-value">
                    +{details.performance.netChange}
                  </span>
                  <span className="tdm-perf-trend up">
                    <TrendingUp size={12} />{" "}
                    {details.performance.netChangeTrend}
                  </span>
                </div>
              </div>

              <div className="tdm-divider" />

              <div className="tdm-section-header-row">
                <h4 className="tdm-section-title">Recent Members</h4>
                <span
                  className="tdm-view-all"
                  onClick={() => setActiveTab("Members")}
                >
                  View all <ArrowRight size={13} />
                </span>
              </div>
              <div className="tdm-members-list">
                {details.recentMembers.map((m, i) => (
                  <div className="tdm-member-row" key={i}>
                    <div className="d-flex align-items-center gap-2">
                      <img
                      src={m.avatar}
                      alt={m.name}
                      className="tdm-member-avatar"
                    />
                    <div>
                      <div className="tdm-member-name">{m.name}</div>
                      <div className="tdm-member-joined">Joined {m.joined}</div>
                    </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === "Benefits" && (
            <>
              <h4 className="tdm-section-title">
                Benefits ({details.benefits.length})
              </h4>
              <div className="tdm-benefits-list">
                {details.benefits.map((b, i) => {
                  const Icon = b.icon;
                  return (
                    <div className="tdm-benefit-row" key={i}>
                      <div className="d-flex align-items-center gap-2">
                        <div className="tdm-benefit-icon">
                          <Icon size={25} color="#14B8A6" />
                        </div>
                        <div className="tdm-benefit-text">
                          <div className="tdm-benefit-title">{b.label}</div>
                          <div className="tdm-benefit-desc">{b.desc}</div>
                        </div>
                      </div>
                      <span className="tdm-benefit-active">Active</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {activeTab === "Members" && (
            <>
              <h4 className="tdm-section-title">
                Members ({tier.members.toLocaleString()})
              </h4>
              <div className="tdm-members-toolbar">
                <div className="tdm-members-search">
                  <Search size={14} color="#9CA3AF" />
                  <input type="text" placeholder="Search members.." />
                </div>
                <button className="tdm-icon-btn">
                  <Filter size={14} />
                </button>
                <button className="tdm-icon-btn">
                  <ArrowUpDown size={14} />
                </button>
              </div>
              <div className="tdm-members-list">
                {details.allMembers.map((m, i) => (
                  <div className="tdm-member-row border border-1 rounded-2 p-2" key={i}>
                    <div className="d-flex align-items-center gap-2">
                      <img
                        src={m.avatar}
                        alt={m.name}
                        className="tdm-member-avatar"
                      />
                      <div>
                        <div className="tdm-member-name">{m.name}</div>
                        <div className="tdm-member-joined">
                          Joined {m.joined}
                        </div>
                      </div>
                    </div>

                    <span className="tdm-view-profile">
                      View Profile <ArrowRight size={12} />
                    </span>
                  </div>
                ))}
              </div>
              <div className="tdm-members-footer">
                <span className="tdm-showing-text">
                  Showing 1-{details.allMembers.length} of{" "}
                  {tier.members.toLocaleString()} members
                </span>
                <div className="tdm-pagination">
                  <button className="tdm-page-arrow" disabled>
                    <ChevronLeft size={14} />
                  </button>
                  <button className="tdm-page-btn active">1</button>
                  <button className="tdm-page-btn">2</button>
                  <span className="tdm-page-ellipsis">...</span>
                  <button className="tdm-page-btn">420</button>
                  <button className="tdm-page-arrow">
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            </>
          )}

          {activeTab === "Activity Log" && (
            <>
              <div className="tdm-activity-header-row">
                <h4 className="tdm-section-title">Activity Log</h4>
                <button className="tdm-period-select">
                  All Activities <ChevronDown size={14} />
                </button>
              </div>
              <div className="tdm-activity-log-list">
                {details.activityLog.map((a, i) => {
                  const Icon = a.icon;
                  return (
                    <div className="tdm-activity-log-row" key={i}>
                      <div
                        className="tdm-activity-log-icon"
                        style={{ backgroundColor: a.bg }}
                      >
                        <Icon size={14} color={a.color} />
                      </div>
                      <div className="tdm-activity-log-text">
                        <div className="tdm-activity-log-title">{a.title}</div>
                        <div className="tdm-activity-log-desc">{a.desc}</div>
                        <div className="tdm-activity-log-date">
                          <Calendar size={11} /> {a.date}
                        </div>
                      </div>
                      <div className="tdm-activity-log-by">
                        <span>by {a.by}</span>
                        <span className="tdm-admin-badge">Admin</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
